import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Sparkles, 
  GraduationCap, 
  Award, 
  BookMarked, 
  ArrowRight,
  ArrowLeft,
  Check,
  Image,
  MessageSquare,
  Users,
  TrendingUp,
  Clock,
  Target,
  Lightbulb,
  Play,
  CheckCircle,
  Lock
} from 'lucide-react';
import { getCourseProgress, getCompletionPercentage, saveCourseProgress } from '../lib/quiz';
import { Link } from 'react-router-dom';

const LearningHub = () => {
  const [showArticle, setShowArticle] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  
  // Placeholder data for learning resources
  const resources = [
    {
      id: 'content-fundamentals',
      title: 'Content Creation Fundamentals',
      description: 'Learn the basics of creating engaging content across different platforms',
      icon: BookOpen,
      comingSoon: false,
      lessons: [
        {
          id: 'lesson-1',
          title: 'Understanding Your Audience',
          duration: '15 min',
          description: 'Learn how to identify and analyze your target audience'
        },
        {
          id: 'lesson-2',
          title: 'Content Strategy Basics',
          duration: '20 min',
          description: 'Develop a solid foundation for your content strategy'
        },
        {
          id: 'lesson-3',
          title: 'Creating Engaging Content',
          duration: '25 min',
          description: 'Master the art of creating content that resonates'
        }
      ]
    },
    {
      id: 'social-strategy',
      title: 'Social Media Strategy',
      description: 'Master the art of social media marketing and audience engagement',
      icon: Sparkles,
      comingSoon: false,
      lessons: [
        {
          id: 'social-1',
          title: 'Platform Selection',
          duration: '10 min',
          description: 'Choose the right platforms for your content'
        },
        {
          id: 'social-2',
          title: 'Content Calendar',
          duration: '15 min',
          description: 'Create an effective posting schedule'
        },
        {
          id: 'social-3',
          title: 'Engagement Tactics',
          duration: '20 min',
          description: 'Learn how to boost engagement on social media'
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Performance',
      description: 'Understand your metrics and optimize your content strategy',
      icon: GraduationCap,
      comingSoon: false,
      lessons: [
        {
          id: 'analytics-1',
          title: 'Key Metrics',
          duration: '15 min',
          description: 'Understanding important performance indicators'
        },
        {
          id: 'analytics-2',
          title: 'Data Analysis',
          duration: '20 min',
          description: 'Learn to analyze and interpret your metrics'
        },
        {
          id: 'analytics-3',
          title: 'Optimization Strategies',
          duration: '25 min',
          description: 'Use data to improve your content strategy'
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Advanced Content Techniques',
      description: 'Take your content to the next level with advanced strategies',
      icon: Award,
      comingSoon: true,
      lessons: []
    },
  ];

  // Load course progress on mount
  useEffect(() => {
    const loadProgress = () => {
      const newProgress: Record<string, number> = {};
      resources.forEach(resource => {
        if (!resource.comingSoon) {
          newProgress[resource.id] = getCompletionPercentage(
            resource.id,
            resource.lessons.length
          );
        }
      });
      setProgress(newProgress);
    };
    
    loadProgress();
  }, []);

  const handleLessonComplete = (courseId: string, lessonId: string) => {
    saveCourseProgress(courseId, lessonId);
    setProgress(prev => ({
      ...prev,
      [courseId]: getCompletionPercentage(courseId, 
        resources.find(r => r.id === courseId)?.lessons.length || 0
      )
    }));
  };

  const renderCourseContent = (course: typeof resources[0]) => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSelectedCourse(null)}
            className="flex items-center space-x-2 text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Learning Hub</span>
          </button>
        </div>
        
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{course.title}</h1>
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-full">
              <course.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {course.lessons.length} lessons
              </span>
              <div className="h-1 w-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {progress[course.id]}% complete
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {course.lessons.map((lesson, index) => {
            const isCompleted = getCourseProgress()
              .find(p => p.courseId === course.id)
              ?.completed.includes(lesson.id);

            return (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-lg p-6 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-900/20">
                        {isCompleted ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <Play className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        )}
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {lesson.title}
                      </h3>
                    </div>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                      {lesson.description}
                    </p>
                    <div className="mt-4 flex items-center space-x-4">
                      <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Clock className="h-4 w-4 mr-1.5" />
                        {lesson.duration}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleLessonComplete(course.id, lesson.id)}
                    className={`ml-4 px-4 py-2 rounded-lg text-sm font-medium ${
                      isCompleted
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                    }`}
                  >
                    {isCompleted ? 'Completed' : 'Mark Complete'}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {selectedCourse ? (
        renderCourseContent(resources.find(r => r.id === selectedCourse)!)
      ) : (
        <>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Learning Hub</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Expand your knowledge with our curated learning resources
            </p>
          </div>

          {/* Featured Course */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-8 text-white">
            <div className="flex items-start justify-between">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <BookMarked className="h-6 w-6" />
                  <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
                    Featured Course
                  </span>
                </div>
                <h2 className="text-2xl font-bold">Content Creator Masterclass</h2>
                <p className="text-indigo-100 max-w-xl">
                  A comprehensive guide to creating, managing, and optimizing your content across all platforms.
                  Coming soon!
                </p>
                <button className="flex items-center space-x-2 px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors">
                  <span>Join Waitlist</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <div className="hidden lg:block">
                {/* Placeholder for course illustration */}
                <div className="w-48 h-48 bg-white/10 rounded-lg"></div>
              </div>
            </div>
          </div>

          {/* Learning Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {resources.map((resource, index) => (
              <motion.div
                key={resource.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-lg p-6 shadow-sm"
              >
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                    <resource.icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {resource.title}
                      </h3>
                      {resource.comingSoon && (
                        <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    {!resource.comingSoon && (
                      <div className="mt-2 w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                        <div
                          className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress[resource.id] || 0}%` }}
                        />
                      </div>
                    )}
                    <p className="mt-2 text-gray-600 dark:text-gray-400">
                      {resource.description}
                    </p>
                    {!resource.comingSoon ? (
                      <button
                        onClick={() => setSelectedCourse(resource.id)}
                        className="mt-4 flex items-center space-x-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                      >
                        <span>Start Learning</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        disabled
                        className="mt-4 flex items-center space-x-2 text-sm font-medium text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      >
                        <span>Start Learning</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Progress Section */}
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Progress</h2>
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">
                Your learning journey will be tracked here. Start a course to see your progress!
              </p>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default LearningHub